import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const emptySite = {
  name: "",
  address: "",
  city: "",
  postal_code: "",
  phone: "",
  email: "",
  manager_name: "",
  notes: "",
  status: "active",
};

export function SiteFormSheet({ open, onOpenChange, companyId, site, onSaved }) {
  const [form, setForm] = useState(emptySite);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    if (site) {
      setForm({
        name: site.name || "",
        address: site.address || "",
        city: site.city || "",
        postal_code: site.postal_code || "",
        phone: site.phone || "",
        email: site.email || "",
        manager_name: site.manager_name || "",
        notes: site.notes || "",
        status: site.status || "active",
      });
    } else {
      setForm(emptySite);
    }
    setError("");
  }, [open, site]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, v === "" ? null : v])
      );
      payload.name = form.name;
      payload.status = form.status;
      let result;
      if (site) {
        const { data } = await api.patch(`/sites/${site._id}`, payload);
        result = data;
        toast.success("Telephely frissítve.");
      } else {
        const { data } = await api.post(`/companies/${companyId}/sites`, payload);
        result = data;
        toast.success("Telephely létrehozva.");
      }
      onOpenChange(false);
      if (onSaved) onSaved(result);
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || "Mentés sikertelen.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-md"
        data-testid="site-form-sheet"
      >
        <SheetHeader className="border-b border-border px-6 py-5">
          <SheetTitle>{site ? "Telephely szerkesztése" : "Új telephely"}</SheetTitle>
          <SheetDescription>
            {site
              ? "Frissítsd a telephely adatait."
              : "Hozz létre új telephelyet ehhez a céghez."}
          </SheetDescription>
        </SheetHeader>
        <form onSubmit={onSubmit} className="flex flex-1 flex-col overflow-hidden">
          <div className="flex-1 space-y-4 overflow-y-auto px-6 py-6">
            <Field label="Megnevezés *" testId="form-site-name">
              <Input
                required
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Budapest központ"
              />
            </Field>
            <Field label="Cím" testId="form-site-address">
              <Textarea
                value={form.address}
                onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                placeholder="1234 Budapest, Példa utca 12."
                rows={2}
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Város" testId="form-site-city">
                <Input
                  value={form.city}
                  onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
                  placeholder="Budapest"
                />
              </Field>
              <Field label="Irányítószám" testId="form-site-postal-code">
                <Input
                  value={form.postal_code}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, postal_code: e.target.value }))
                  }
                  placeholder="1234"
                  className="font-mono"
                />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="E-mail" testId="form-site-email">
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  placeholder="bp@sipx.hu"
                />
              </Field>
              <Field label="Telefon" testId="form-site-phone">
                <Input
                  value={form.phone}
                  onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                  placeholder="+36 1 234 5678"
                />
              </Field>
            </div>
            <Field label="Telephely vezető" testId="form-site-manager-name">
              <Input
                value={form.manager_name}
                onChange={(e) =>
                  setForm((f) => ({ ...f, manager_name: e.target.value }))
                }
                placeholder="Kis Pál"
              />
            </Field>
            <Field label="Állapot" testId="form-site-status">
              <Select
                value={form.status}
                onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Aktív</SelectItem>
                  <SelectItem value="inactive">Inaktív</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Megjegyzés" testId="form-site-notes">
              <Textarea
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                rows={2}
              />
            </Field>
            {error && (
              <div
                data-testid="form-error"
                className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive"
              >
                {error}
              </div>
            )}
          </div>
          <SheetFooter className="flex-row justify-end gap-2 border-t border-border bg-[hsl(var(--sipx-elevated))] px-6 py-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="cancel-site-form"
            >
              Mégse
            </Button>
            <Button type="submit" disabled={saving} data-testid="submit-site-form">
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Mentés…
                </>
              ) : site ? (
                "Mentés"
              ) : (
                "Létrehozás"
              )}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}

function Field({ label, children, testId }) {
  return (
    <label className="block space-y-1.5" data-testid={testId}>
      <span className="block text-xs font-medium text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
